import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import { IconArrowRightRound } from '@/components/apps/ui/icon';
import ArtDirectedImage, {
  type ArtDirectedImageSource,
} from '@/components/shared/ui/ArtDirectedImage';

/**
 * ### ListenAiCardData
 *
 * One solution as the card presents it: the copy key of its title, whose
 * first line is the promise and second the product name; the copy key of
 * its description, which is either a run of hashtags or a sentence; its
 * art-directed image; and where the card leads.
 */
export interface ListenAiCardData {
  titleKey: string;
  descriptionKey: string;
  image: {
    desktop: ArtDirectedImageSource;
    mobile: ArtDirectedImageSource;
  };
  href: string;
}

interface ListenAiCardProps {
  card: ListenAiCardData;
  /** Position in the row, from one, printed in the corner */
  ordinal: number;
  /** How many cards are in the row, printed after the ordinal */
  count: number;
  /** Image `sizes` hint shared by the row */
  sizes: string;
}

/**
 * Splits a description into hashtags when it is written as hashtags, so
 * they can be set as separate tags. A sentence stays whole.
 */
function splitTags(description: string): Array<string> | undefined {
  if (!description.trimStart().startsWith('#')) return undefined;

  return description
    .split(/\s+#/)
    .map((tag) => tag.replace(/^#/, '').trim())
    .filter((tag) => tag.length > 0);
}

/**
 * ### ListenAiCard
 *
 * One solution card, and the whole of it is the link: the image, the copy
 * and the arrow all sit inside one anchor, so wherever the card is pressed
 * it opens the solution. The product name is the card's headline and the
 * promise sits above it as a lead, so the three cards read as three named
 * products rather than three sentences. Hashtag descriptions are set as
 * tags; a sentence is set as a sentence. The image draws in and the arrow
 * slides on hover, and both stay put under reduced motion.
 *
 * @returns The rendered card link
 */
export default function ListenAiCard({
  card,
  ordinal,
  count,
  sizes,
}: ListenAiCardProps) {
  const { t } = useTranslation('common');
  const [lead, name = ''] = t(card.titleKey).split('\n');
  const description = t(card.descriptionKey);
  const tags = splitTags(description);
  const readLabel = t('자세히 보기');

  return (
    <Link
      href={card.href}
      prefetch={false}
      className="group relative isolate flex h-[440px] min-w-0 flex-col justify-end overflow-hidden rounded-[24px] bg-apps-coreGrayBlack text-apps-coreGrayWhite shadow-[0_12px_48px_rgba(0,0,0,0.12)] transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_24px_64px_rgba(0,0,0,0.22)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-corePrimary01 motion-reduce:transform-none motion-reduce:transition-none md:h-[clamp(440px,52vw,680px)] lg:h-[clamp(460px,42vw,720px)]"
      aria-label={`${name || lead} ${readLabel}`}
    >
      <ArtDirectedImage
        className="absolute inset-0 -z-10 block h-full w-full transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transform-none motion-reduce:transition-none [&_img]:h-full [&_img]:w-full [&_img]:object-cover"
        desktop={card.image.desktop}
        mobile={card.image.mobile}
        alt=""
        quality={82}
        sizes={sizes}
      />
      {/*
       * Three washes: a light one over the top so the corner reads, a
       * vignette that closes in from the sides over the lower half, and a
       * deep one rising from the bottom under the copy. The copy sits on the
       * lower half, so that is where the image gives way to it.
       */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(0,0,0,0.3)_0%,rgba(0,0,0,0)_26%),radial-gradient(120%_70%_at_50%_100%,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0.35)_45%,rgba(0,0,0,0)_75%),linear-gradient(180deg,rgba(0,0,0,0)_40%,rgba(0,0,0,0.6)_66%,rgba(0,0,0,0.92)_100%)] transition-opacity duration-300 group-hover:opacity-95"
      />

      <span className="absolute left-6 top-6 inline-flex items-center gap-2 text-[13px] font-bold leading-none tracking-[0.04em] text-white/80 lg:left-[clamp(24px,3vw,40px)] lg:top-[clamp(24px,3vw,40px)] lg:text-[clamp(13px,1vw,15px)]">
        <span className="[font-variant-numeric:tabular-nums]">
          {String(ordinal).padStart(2, '0')}
        </span>
        <span className="h-px w-5 bg-white/40" aria-hidden="true" />
        <span className="text-white/55 [font-variant-numeric:tabular-nums]">
          {String(count).padStart(2, '0')}
        </span>
      </span>

      <span className="flex flex-col gap-5 p-6 lg:gap-[clamp(20px,2.5vw,32px)] lg:p-[clamp(24px,3.6vw,48px)]">
        <span className="flex flex-col gap-2 lg:gap-[clamp(8px,0.9vw,12px)]">
          <span className="text-pretty text-[15px] font-semibold leading-[150%] tracking-[-0.3px] text-white/78 lg:text-[clamp(15px,1.25vw,19px)]">
            {lead}
          </span>
          <strong className="text-balance text-[28px] font-bold leading-[118%] tracking-[-0.03em] lg:text-[clamp(28px,2.9vw,44px)]">
            {name || lead}
          </strong>
        </span>

        {tags ? (
          <span className="flex flex-wrap gap-1.5 lg:gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[12px] font-semibold leading-[140%] tracking-[-0.12px] text-white/90 backdrop-blur-sm lg:px-3 lg:text-[clamp(12px,0.95vw,14px)]"
              >
                {tag}
              </span>
            ))}
          </span>
        ) : (
          <span className="max-w-[34ch] text-pretty text-[15px] font-medium leading-[160%] tracking-[-0.3px] text-white/85 lg:text-[clamp(15px,1.2vw,18px)]">
            {description}
          </span>
        )}

        <span className="mt-1 flex items-center justify-between border-t border-white/15 pt-4 lg:pt-5">
          <span className="text-[14px] font-bold leading-none tracking-[-0.14px] lg:text-[clamp(14px,1.05vw,16px)]">
            {readLabel}
          </span>
          <span
            aria-hidden="true"
            className="inline-flex size-10 items-center justify-center rounded-full bg-white text-apps-coreGrayBlack transition-transform duration-300 ease-out group-hover:translate-x-1 motion-reduce:transform-none motion-reduce:transition-none lg:size-11"
          >
            <IconArrowRightRound size={20} />
          </span>
        </span>
      </span>
    </Link>
  );
}
